import { useParams } from "react-router-dom";
import { useMemo } from "react";
import { TimeSeriesChartItem } from "@iliotech/widgets";
import { ClickableLegendItem } from "@iliotech/component-library";
import { useGlobalCurrency, useStructureGrouping } from "@iliotech/data-wire";
import { useMonthlyPnlByAllocation } from "@iliotech/data-wire/src/reactHooks/api/useMonthlyPnlByAllocation";
import { AllocationOption } from "@iliotech/data-wire/src/reactHooks/context/StructureGrouping/types";
import { ITimePeriod } from "@iliotech/data-wire/src/reactHooks/context/types";

const labelMapper = new Map([
  ["CashAndEquivalents", "Cash Balances"],
  ["FixedIncome", "Fixed Income"],
  ["RealEstate", "Real Estate"],
  ["CryptoCurrencies", "Crypto-Currencies"],
]);

export const useMonthlyPnl = ({
  overriddenGrouping,
  overriddenSelectedGroup,
  overriddenPeriod,
}: {
  overriddenGrouping?: AllocationOption;
  overriddenSelectedGroup?:
    | {
        code: string;
        label: string;
      }
    | undefined;
  overriddenPeriod?: ITimePeriod;
}) => {
  const { portfolioId = "" } = useParams();
  const pnlByAllocation = useMonthlyPnlByAllocation(
    portfolioId,
    overriddenPeriod
  );
  const { currencyFormatter } = useGlobalCurrency();
  const { grouping: contextGrouping, selectedGroup: contextSelectedGroup } =
    useStructureGrouping();

  const grouping = overriddenGrouping || contextGrouping;
  const selectedGroup = overriddenSelectedGroup || contextSelectedGroup;

  const { data, isLoading, legend } = useMemo(() => {
    const data: TimeSeriesChartItem[] =
      pnlByAllocation?.data?.data?.data
        ?.filter(
          (item) => !selectedGroup || item.assetClass === selectedGroup?.code
        )
        .map((item) => ({
          category: item.assetClass,
          entries: item.data.map((entry) => ({
            date: new Date(entry.date),
            value: entry.value,
          })),
          color: grouping?.getColor?.(item.assetClass),
        })) || [];

    const legend: ClickableLegendItem[] = [
      {
        label: "Total",
        value: currencyFormatter(pnlByAllocation?.data?.data?.total || 0),
      },
      ...(pnlByAllocation?.data?.data?.data || []).map((item) => ({
        label: labelMapper.get(item.assetClass) || item.assetClass,
        color: grouping?.getColor?.(item.assetClass),
        value: currencyFormatter(item.assetClassTotal),
      })),
    ];

    return {
      data,
      isLoading: pnlByAllocation?.isLoading,
      legend,
    };
  }, [pnlByAllocation.data, selectedGroup, grouping, currencyFormatter]);

  return { data, isLoading, legend, currencyFormatter };
};
